/**
 * SafetySheet — bottom sheet shown when the user taps the red "I need help" button.
 * Offers two parallel actions: call 112 directly, and notify Waylo + emergency contact.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { triggerSafetyAlertApi } from '../lib/api';
import { useSession } from '../lib/session';
import { translateError, useT } from '../lib/i18n';
import { hapticError, hapticSuccess } from '../lib/telegram';
import { TextArea } from './TextInput';
import { BottomSheet } from './BottomSheet';

interface Props {
  bookingId: string;
  onClose: () => void;
}

export function SafetySheet({ bookingId, onClose }: Props): JSX.Element {
  const { t } = useT();
  const { me } = useSession();
  const [note, setNote] = useState('');
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [resultOk, setResultOk] = useState(false);

  const notify = useMutation({
    mutationFn: () =>
      triggerSafetyAlertApi(bookingId, { note: note || undefined, notifyEmergencyContact: true }),
    onSuccess: () => {
      hapticSuccess();
      setResultOk(true);
      setResultMsg(t('safety.sheet.notifySent'));
    },
    onError: (e: Error) => {
      hapticError();
      setResultOk(false);
      setResultMsg(translateError(t, e));
    },
  });

  return (
    <BottomSheet onClose={onClose} title={t('safety.sheet.title')}>
      <>
        <a
          href="tel:112"
          className="mb-3 block rounded-card bg-destructive p-4 text-white"
        >
          <p className="text-base font-semibold">{t('safety.sheet.callEmergency')}</p>
          <p className="mt-1 text-xs opacity-90">{t('safety.sheet.callEmergencyHint')}</p>
        </a>

        <div className="mb-3 space-y-2 rounded-card border border-[var(--border-light)] bg-tg-bg-secondary p-4">
          <p className="text-base font-semibold text-tg-text">{t('safety.sheet.notifyWaylo')}</p>
          <p className="text-xs text-tg-hint">{t('safety.sheet.notifyWayloHint')}</p>
          <TextArea
            id="safety-note"
            label={t('safety.sheet.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder={t('safety.sheet.notePh')}
          />
          <button
            type="button"
            onClick={() => notify.mutate()}
            disabled={notify.isPending}
            className="w-full rounded-input bg-brand-navy py-3 text-sm font-semibold text-brand-cream disabled:opacity-50"
          >
            {notify.isPending ? '…' : t('safety.openSheet')}
          </button>

          {resultMsg && (
            <div
              className={
                'mt-2 rounded-input p-3 text-sm ' +
                (resultOk
                  ? 'bg-brand-green/15 text-brand-green'
                  : 'bg-destructive/10 text-destructive')
              }
            >
              {resultMsg}
              {resultOk && me?.emergencyContactPhone && (
                <a
                  href={`tel:${me.emergencyContactPhone}`}
                  className="mt-2 block rounded-input bg-brand-navy px-3 py-2 text-center text-sm font-semibold text-brand-cream"
                >
                  {t('safety.sheet.callContactBtn', {
                    name: me.emergencyContactName ?? me.emergencyContactPhone,
                  })}
                </a>
              )}
            </div>
          )}
        </div>
      </>
    </BottomSheet>
  );
}
